import { computed, makeAutoObservable } from "mobx";
import RecurringIcon from "../../../../../images/membership/recurring.svg";
import TermRecurringIcon from "../../../../../images/membership/term-recurring.svg";
import TermIcon from "../../../../../images/membership/term.svg";
import { ItemBillingType } from "../enums";
import ItemBenefitsService from "./ItemBenefitsService";
import {
  CustomPricingInputModel,
  PackagePricingInputModel,
  PricePageRecurringInputModel,
  PricePageTermsInputModel,
  PriceTierInputData,
  PricingPageInputModel,
  StandardPricingInputModel,
} from "../models/PriceInputs.model";
import { IAPICreateUpdatePriceModel } from "../interfaces/IAPIPrice.model";

type BillingTypesData = {
  label: string;
  icon: string;
  key: ItemBillingType;
  activeClass: string;
};
export default class PricingPageService {
  public prices: PricingPageInputModel[];
  public editIndex: number = 0;
  public benefitsService: ItemBenefitsService;
  constructor() {
    this.prices = [
      new PricingPageInputModel({
        name: "New Price",
        referenceId: new Date().getTime().toString(),
        isOpen: true,
        isDefault: true,
        isActive: true,
        billingType: ItemBillingType.Term,
        termSettings: new PricePageTermsInputModel({
          standardPricing: new StandardPricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
          customPricing: new CustomPricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
          tieredPricing: new PriceTierInputData({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
          packagePricing: new PackagePricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
          volumePricing: new PriceTierInputData({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
        }),
        recurringSettings: new PricePageRecurringInputModel({
          standardPricing: new StandardPricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: false,
          }),
          customPricing: new CustomPricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: false,
          }),
          tieredPricing: new PriceTierInputData({
            showSetUpFee: true,
            isSetUpFeeRequired: false,
          }),
          packagePricing: new PackagePricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: false,
          }),
          volumePricing: new PriceTierInputData({
            showSetUpFee: true,
            isSetUpFeeRequired: false,
          }),
        }),
        isTermsOpen: true,
        isAdvancedOpen: true,
        isRecurringOpen: true,
      }),
    ];
    this.benefitsService = new ItemBenefitsService();
    makeAutoObservable(this);
  }

  updatePrice(data: PricingPageInputModel, index: number) {
    const currentPrice = this.prices[index];
    if (currentPrice?.billingType !== data.billingType) {
      switch (data.billingType) {
        case ItemBillingType.Term:
          if (!data.termSettings) {
            data.termSettings = new PricePageTermsInputModel({
              standardPricing: new StandardPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
              customPricing: new CustomPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
              tieredPricing: new PriceTierInputData({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
              packagePricing: new PackagePricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
              volumePricing: new PriceTierInputData({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
            });
          }
          break;
        case ItemBillingType.Recurring:
          if (!data.recurringSettings) {
            data.recurringSettings = new PricePageRecurringInputModel({
              standardPricing: new StandardPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
              customPricing: new CustomPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
              tieredPricing: new PriceTierInputData({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
              packagePricing: new PackagePricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
              volumePricing: new PriceTierInputData({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
            });
          }

          break;
        case ItemBillingType.TermPlusRecurring:
          if (!data.termSettings) {
            data.termSettings = new PricePageTermsInputModel({
              standardPricing: new StandardPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: true,
              }),
            });
          }
          if (!data.recurringSettings) {
            data.recurringSettings = new PricePageRecurringInputModel({
              standardPricing: new StandardPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
              customPricing: new CustomPricingInputModel({
                showSetUpFee: true,
                isSetUpFeeRequired: false,
              }),
            });
          }
          break;
      }
    }
    this.prices[index] = new PricingPageInputModel(data);

    this.prices = this.prices.map((p) => {
      return new PricingPageInputModel(p);
    });
  }

  @computed
  billingOptions(index: number): BillingTypesData[] {
    const billingType = this.prices[index]?.billingType;
    return [
      {
        icon: TermIcon,
        label: "Term",
        activeClass: billingType === ItemBillingType.Term ? "active" : "",
        key: Object.keys(ItemBillingType).find(
          (key) =>
            ItemBillingType[key as keyof typeof ItemBillingType] ===
            ItemBillingType.Term
        ),
      } as BillingTypesData,
      {
        icon: RecurringIcon,
        label: "Recurring",
        activeClass: billingType === ItemBillingType.Recurring ? "active" : "",
        key: Object.keys(ItemBillingType).find(
          (key) =>
            ItemBillingType[key as keyof typeof ItemBillingType] ===
            ItemBillingType.Recurring
        ),
      } as BillingTypesData,
      {
        icon: TermRecurringIcon,
        label: "Term + Recurring",
        activeClass:
          billingType === ItemBillingType.TermPlusRecurring ? "active" : "",
        key: Object.keys(ItemBillingType).find(
          (key) =>
            ItemBillingType[key as keyof typeof ItemBillingType] ===
            ItemBillingType.TermPlusRecurring
        ),
      } as BillingTypesData,
    ];
  }

  validate(): boolean {
    let hasErrors = false;
    this.prices.forEach((price) => {
      const isValid = price.validate(false);
      if (!isValid) {
        hasErrors = true;
      }
    });
    return !hasErrors;
  }

  copyPrice(index: number) {
    const price = this.prices[index];

    this.prices = this.prices.map((p) => {
      p.isOpen = false;
      return new PricingPageInputModel(p);
    });
    this.prices.splice(
      index + 1,
      0,
      new PricingPageInputModel({
        ...price,
        id: "",
        isDefault: false,
        isOpen: true,
        referenceId: new Date().getTime().toString(),
      })
    );
  }

  addPrice() {
    this.prices = this.prices.map((p) => {
      p.isOpen = false;
      return new PricingPageInputModel(p);
    });
    this.prices.push(
      new PricingPageInputModel({
        name: "New Price",
        referenceId: new Date().getTime().toString(),
        isOpen: true,
        isActive: true,
        billingType: ItemBillingType.Term,
        termSettings: new PricePageTermsInputModel({
          standardPricing: new StandardPricingInputModel({
            showSetUpFee: true,
            isSetUpFeeRequired: true,
          }),
        }),
        isTermsOpen: true,
        isAdvancedOpen: true,
        isRecurringOpen: true,
      })
    );
  }

  removePrice(index: number) {
    this.prices.splice(index, 1);
  }

  createAPIModel(): IAPICreateUpdatePriceModel[] {
    return this.prices.map((price) => price.createAPIModel());
  }

  static deserializeAPIModel(
    data: IAPICreateUpdatePriceModel[]
  ): PricingPageInputModel[] {
    const list: PricingPageInputModel[] = [];

    if (Array.isArray(data)) {
      data.forEach((d) => {
        const mappedData = PricingPageInputModel.deserializeAPIModel(d);
        list.push(mappedData);
      });
    }
    return list;
  }
}
